import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Library, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

export function BoxSetDialog({ series }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [order, setOrder] = useState([]);
  const [on, setOn] = useState({});
  const [title, setTitle] = useState("");
  const [parts, setParts] = useState(true);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState([]);
  useEffect(() => {
    if (!open) return;
    setOrder(series.books); setOn(Object.fromEntries(series.books.map((b) => [b.id, true]))); setTitle(`${series.name} Box Set`);
    api.get(`/series/${series.id}/box-sets`).then((r) => setExisting(r.data));
  }, [open, series]);
  const move = (i, d) => { const o = [...order]; [o[i], o[i + d]] = [o[i + d], o[i]]; setOrder(o); };
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/series/${series.id}/box-set`, { title, book_ids: order.filter((b) => on[b.id]).map((b) => b.id), part_pages: parts });
      toast.success(`Box set created: ${data.chapter_count} sections, ${data.word_count.toLocaleString()} words. Approve v1 to build editions.`);
      nav(`/app/books/${data.book_id}?tab=versions`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="mt-4 border-slate-700" disabled={series.books.length < 2} onClick={() => setOpen(true)} data-testid={`series-boxset-${series.id}`}><Library size={13} className="mr-1" />Create box set</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 max-w-lg" data-testid="boxset-dialog">
          <DialogHeader><DialogTitle className="font-display">Series box set</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400">Combines each book's <b>approved</b> version into a new book in your library with one shared contents page. The original books are never changed.</p>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="boxset-title-input" />
          <div className="space-y-1">
            {order.map((b, i) => (
              <div key={b.id} className="flex items-center gap-2 text-sm border border-slate-800 rounded-lg px-3 py-2" data-testid={`boxset-book-${b.id}`}>
                <input type="checkbox" checked={!!on[b.id]} onChange={(e) => setOn({ ...on, [b.id]: e.target.checked })} data-testid={`boxset-include-${b.id}`} />
                <span className="font-mono text-amber-500/80">#{b.book_number ?? "?"}</span><span className="flex-1 truncate">{b.title}</span>
                <button disabled={i === 0} onClick={() => move(i, -1)} className="text-slate-500 disabled:opacity-30" data-testid={`boxset-up-${b.id}`}><ArrowUp size={13} /></button>
                <button disabled={i === order.length - 1} onClick={() => move(i, 1)} className="text-slate-500 disabled:opacity-30" data-testid={`boxset-down-${b.id}`}><ArrowDown size={13} /></button>
              </div>
            ))}
          </div>
          <label className="text-xs text-slate-300 flex gap-2"><input type="checkbox" checked={parts} onChange={(e) => setParts(e.target.checked)} data-testid="boxset-part-pages" />Start each book with a "Book N: Title" part page</label>
          {existing.length > 0 && <p className="text-[11px] text-slate-500">Existing box sets: {existing.map((x) => x.title).join(", ")}</p>}
          <Button onClick={create} disabled={busy || order.filter((b) => on[b.id]).length < 2} className="bg-amber-600 hover:bg-amber-500" data-testid="boxset-create-button">{busy ? "Assembling…" : "Create box set book"}</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
